import { useState } from "react";
import { cn } from "@/utils/cn";
import type { CostBreakdown } from "@/utils/formulation/computeCost";
import { KpiTile } from "./KpiTile";
import { CostReasonsPopover } from "./CostReasonsPopover";

const UNITS = [
  { id: "10g",   label: "/10g",   factor: 0.1 },
  { id: "100g",  label: "/100g",  factor: 1 },
  { id: "1kg",   label: "/1kg",   factor: 10 },
  { id: "batch", label: "/batch", factor: 100 },
] as const;

type UnitId = (typeof UNITS)[number]["id"];

// Always 2-decimal, with thousands separators for plain dollars and k/M
// suffixes once the value would otherwise show 5+ integer digits. The k
// threshold also re-checks after rounding so a value like 999,999.999
// rolls to "$1.00M" instead of the awkward "$1000.00k".
function formatCost(value: number): string {
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e4) {
    const kRounded = Number((value / 1e3).toFixed(2));
    if (kRounded >= 1000) return `$${(value / 1e6).toFixed(2)}M`;
    return `$${kRounded.toFixed(2)}k`;
  }
  return `$${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

type CostTileProps = {
  cost: CostBreakdown | null;
  unit: UnitId;
  setUnit: (u: UnitId) => void;
  useMedianFallback: boolean;
  setUseMedianFallback: (next: boolean) => void;
  medianPricePerGramUsd: number | null;
  resolveName: (compoundId: string) => string;
};

export function CostTile({
  cost, unit, setUnit,
  useMedianFallback, setUseMedianFallback,
  medianPricePerGramUsd, resolveName,
}: CostTileProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const unitDef = UNITS.find((u) => u.id === unit) || UNITS[1];
  const empty = !cost || cost.totalCompounds === 0 || cost.usdPer100g === null;
  const value = empty ? null : cost!.usdPer100g! * unitDef.factor;

  const cycle = () => {
    const idx = UNITS.findIndex((u) => u.id === unit);
    setUnit(UNITS[(idx + 1) % UNITS.length].id);
  };

  const issues = cost
    ? cost.unpricedCount + cost.noMwCount + cost.medianCount
    : 0;

  return (
    <KpiTile
      onClick={cycle}
      label={
        <span style={{ display: "inline-flex", alignItems: "baseline", gap: 3 }}>
          <span>Cost</span>
          <span style={{ color: "var(--ink-4)", fontWeight: 400 }}>{unitDef.label}</span>
        </span>
      }
      tooltip={{
        label: empty
          ? "Cost · Empty flask"
          : `Cost · ${formatCost(value!)} ${unitDef.label}`,
        sublabel: empty
          ? "Add priced compounds to see live cost roll-up."
          : `Cheapest in-scope supplier × mass fraction (basis 100g). Click to cycle unit.`,
      }}
    >
      <span
        className={cn("kpi-num", empty && "empty")}
        style={
          cost && cost.medianCount > 0
            ? { textDecoration: "underline dotted", textUnderlineOffset: 3 }
            : undefined
        }
      >
        {empty ? "—" : formatCost(value!)}
      </span>
      {issues > 0 && cost && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setPopoverOpen((v) => !v); }}
          title={`${cost.unpricedCount} unpriced · ${cost.noMwCount} no-MW · ${cost.medianCount} median`}
          style={{
            fontSize: 9, color: "var(--brand-amber)", marginLeft: 4,
            background: "transparent", border: "none", cursor: "pointer", padding: 0,
          }}
        >
          ⚠
        </button>
      )}
      {popoverOpen && cost && (
        <div
          style={{
            position: "absolute", top: "100%", left: 0, zIndex: 50,
            marginTop: 4, background: "var(--bg)",
            border: "1px solid var(--line)", borderRadius: 4,
            boxShadow: "var(--shadow-2)",
          }}
        >
          <CostReasonsPopover
            cost={cost}
            resolveName={resolveName}
            useMedianFallback={useMedianFallback}
            onToggleMedianFallback={setUseMedianFallback}
            medianAvailable={medianPricePerGramUsd !== null}
          />
        </div>
      )}
    </KpiTile>
  );
}
